/**
 * Deeply merges two objects, with obj2 taking precedence over obj1.
 * Arrays and non-object values are replaced rather than merged.
 *
 * @param obj1 - The base object to merge into
 * @param obj2 - The object to merge on top of obj1
 * @returns A new object that is the deep merge of obj1 and obj2
 */
export function deepMerge<T extends Record<string, any>>(
  obj1: T | undefined,
  obj2: Partial<T> | undefined
): T {
  if (obj1 !== undefined && obj2 === undefined) {
    return obj1;
  }

  if (obj2 !== undefined && obj1 === undefined) {
    return obj2 as T;
  }

  if (
    !obj1 ||
    typeof obj1 !== 'object' ||
    Array.isArray(obj1) ||
    !obj2 ||
    typeof obj2 !== 'object' ||
    Array.isArray(obj2)
  ) {
    return obj2 as T;
  }

  const result: Record<string, any> = {};
  const obj2Keys = new Set(Object.keys(obj2));

  // Iterate over all keys from both objects
  const allKeys = Array.from(
    new Set([...Object.keys(obj1), ...Object.keys(obj2)])
  );

  for (const key of allKeys) {
    if (!obj2[key] && obj2Keys.has(key)) {
      result[key] = obj2[key];

      if (typeof result[key] === 'undefined') {
        delete result[key];
      }

      continue;
    }

    result[key] = deepMerge(obj1[key], obj2[key]);
  }

  return result as T;
}

/**
 * Traverses an object recursively and applies a callback function to transform it.
 * Handles arrays, objects, and primitives.
 *
 * @template T - The input type (can be any value)
 * @template R - The return type of the callback
 * @param obj - The object to traverse (can be object, array, or primitive)
 * @param callback - Function that receives the transformed object and returns a new value
 * @returns The result of applying the callback to the traversed object
 */
export function traverseMapObject<T, R>(
  obj: T,
  callback: (obj: Record<string, any>) => R
): T extends any[] ? R[] : R {
  // Array
  if (Array.isArray(obj)) {
    return obj.map((child) =>
      traverseMapObject(child, callback)
    ) as T extends any[] ? R[] : R;
  }

  // Non-object or null/undefined
  if (!obj || typeof obj !== 'object') {
    return obj as unknown as T extends any[] ? R[] : R;
  }

  const newObj: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const child = (obj as Record<string, any>)[key];
      newObj[key] = traverseMapObject(child, callback);
    }
  }

  const result = callback(newObj);
  return result as T extends any[] ? R[] : R;
}
